import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { boosterData } from "../services/BoosterServices";
import { useSearchParams } from "react-router-dom";

export interface boosterQuantity {
  booster: boosterData;
  quantity: number;
  nbCards: number;
}

interface PrintCartContextType {
  items: boosterQuantity[];
  addToCart: (item: boosterQuantity) => void;
  removeFromCart: (booster: boosterData) => void;
  increaseItemQuantity: (item: boosterQuantity) => void;
  decreaseItemQuantity: (item: boosterQuantity) => void;
  resetCart: () => void;
}

const PrintCartContext = createContext<PrintCartContextType | undefined>(
  undefined
);

export const PrintCartProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [items, setItems] = useState<boosterQuantity[]>(() => {
    const savedCart = localStorage.getItem("PrintCart");
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const checkoutSuccess = searchParams.get("checkout_success");
    const isTypePrint = searchParams.get("type") === "print";
    if (checkoutSuccess && isTypePrint) {
      setItems([]);
    }
  }, [searchParams]);

  useEffect(() => {
    localStorage.setItem("PrintCart", JSON.stringify(items));
  }, [items]);

  const addToCart = (item: boosterQuantity) => {
    setItems((prev) => {
      const isDuplicate = prev.some(
        (existingItem) => existingItem.booster.id === item.booster.id
      );

      if (isDuplicate) {
        console.log("Item is already in the cart!");
        return prev;
      }

      return [...prev, item];
    });
  };
  const removeFromCart = (booster: boosterData) => {
    setItems(items.filter((item) => item.booster !== booster));
  };

  const increaseItemQuantity = (item: boosterQuantity) => {
    setItems((prevItems) =>
      prevItems.map((i) =>
        i.booster.id === item.booster.id
          ? { ...i, quantity: Math.min(i.quantity + 1, 16) }
          : i
      )
    );
  };

  const decreaseItemQuantity = (item: boosterQuantity) => {
    setItems((prevItems) =>
      prevItems.map((i) =>
        i.booster.id === item.booster.id
          ? { ...i, quantity: Math.max(i.quantity - 1, 1) }
          : i
      )
    );
  };

  const resetCart = () => {
    setItems([]);
    localStorage.removeItem("PrintCart");
  };

  return (
    <PrintCartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        increaseItemQuantity,
        decreaseItemQuantity,
        resetCart,
      }}
    >
      {children}
    </PrintCartContext.Provider>
  );
};

export const usePrintCartProvider = (): PrintCartContextType => {
  const context = useContext(PrintCartContext);
  if (!context) {
    throw new Error(
      "usePrintCartProvider must be used within a PrintCartProvider"
    );
  }
  return context;
};
